import { useState, useRef, useCallback } from 'react';

const MAX_EXTRACTED_TEXT_LENGTH = 200000;

export interface AttachedFile {
  id: string;
  file: File;
  status: 'extracting' | 'ready' | 'error';
  extractedText?: string;
  errorMessage?: string;
  pageCount?: number;
}

export function useFileExtraction() {
  const [attachedFiles, setAttachedFiles] = useState<AttachedFile[]>([]);

  const addFiles = useCallback(async (files: File[]) => {
    const { extractTextFromFile } = await import('@/services/textExtractionService');

    // Devolvemos el array mapeado de una vez para que React actualice de forma limpia
    const newFiles: AttachedFile[] = await Promise.all(
      files.map(async (file) => {
        const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

        // Intentar extracción local
        try {
          const result = await extractTextFromFile(file);
          if (result.success) {
            return {
              id,
              file,
              status: 'ready' as const,
              extractedText: result.text
            };
          }
        } catch (e) {
          console.warn(`Local extraction failed for ${file.name}, will try VPS:`, e);
        }

        // Si falla localmente o no es soportado, se marca como listo para subirse al VPS
        return { id, file, status: 'ready' as const };
      })
    );

    setAttachedFiles(prev => [...prev, ...newFiles]);
  }, []);

  const removeFile = useCallback((id: string) => {
    setAttachedFiles(prev => prev.filter(f => f.id !== id));
  }, []);

  const clearAll = useCallback(() => {
    setAttachedFiles([]);
  }, []);

  const allReady = attachedFiles.length === 0 || attachedFiles.every(f => f.status !== 'extracting');
  const hasAnyReady = attachedFiles.some(f => f.status === 'ready');
  const readyFiles = attachedFiles.filter(f => f.status === 'ready');

  return { attachedFiles, addFiles, removeFile, clearAll, allReady, hasAnyReady, readyFiles };
}
